/**
 * @fileoverview Section "Typography" — échelle Inter Variable + JetBrains Mono.
 *
 * Illustre les tailles utilisées dans les apps, les poids typographiques
 * autorisés, et l'usage de JetBrains Mono pour les identifiants techniques
 * (LIQ, `merchant_reference` ULID, UUID, `trace_id`).
 */

import { DsPreview } from '../components/DsPreview.tsx';

const SIZES = [
  { class: 'text-5xl', label: 'text-5xl', role: 'Page hero (rare)' },
  { class: 'text-4xl', label: 'text-4xl', role: 'Titres de page' },
  { class: 'text-3xl', label: 'text-3xl', role: 'H1 courants (PageHeader)' },
  { class: 'text-2xl', label: 'text-2xl', role: 'H2 (SectionHeader)' },
  { class: 'text-xl', label: 'text-xl', role: 'H3, MetricCard value' },
  { class: 'text-lg', label: 'text-lg', role: 'H4, sub-titres' },
  { class: 'text-base', label: 'text-base', role: 'Corps de texte' },
  { class: 'text-sm', label: 'text-sm', role: 'Corps dense, tableaux' },
  { class: 'text-xs', label: 'text-xs', role: 'Labels, badges, captions' },
] as const;

const WEIGHTS = [
  { class: 'font-normal', label: '400 · normal' },
  { class: 'font-medium', label: '500 · medium' },
  { class: 'font-semibold', label: '600 · semibold' },
  { class: 'font-bold', label: '700 · bold' },
] as const;

export function DsSectionTypography(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">
          Échelle — <span className="font-mono">Inter Variable</span>
        </h3>
        <div className="rounded-lg border border-border">
          {SIZES.map((s) => (
            <div
              key={s.class}
              className="flex items-baseline justify-between gap-6 border-b border-border px-6 py-4 last:border-b-0"
            >
              <span className={`${s.class} text-quipux-navy-950`}>Guichet National GNPC</span>
              <div className="text-right">
                <code className="font-mono text-xs text-quipux-navy-800">{s.label}</code>
                <p className="text-xs text-muted-foreground">{s.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Poids</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {WEIGHTS.map((w) => (
            <DsPreview key={w.class} label={w.class} align="center">
              <span className={`text-2xl text-quipux-navy-950 ${w.class}`}>Aa</span>
            </DsPreview>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">
          Mono — <span className="font-mono">JetBrains Mono Variable</span>
        </h3>
        <p className="text-xs text-muted-foreground">
          Réservée aux identifiants techniques où l'ambiguïté <code>0/O</code>, <code>1/l/I</code>,{' '}
          <code>S/5</code> peut causer un écart de conciliation.
        </p>
        <div className="grid gap-3 rounded-lg border border-border p-6 font-mono text-sm text-quipux-navy-950">
          <div className="flex items-center justify-between">
            <span>LIQ-921555</span>
            <span className="text-xs text-muted-foreground">Numéro de liquidation</span>
          </div>
          <div className="flex items-center justify-between">
            <span>01HXK9M0FZQR8Y3V6TZ2WC5B1Q</span>
            <span className="text-xs text-muted-foreground">merchant_reference (ULID)</span>
          </div>
          <div className="flex items-center justify-between">
            <span>0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b5c</span>
            <span className="text-xs text-muted-foreground">UUIDv7</span>
          </div>
          <div className="flex items-center justify-between tabular-nums">
            <span>2 000 000 F CFA</span>
            <span className="text-xs text-muted-foreground">Montant XOF (tabular-nums)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
